/**
 * @fileoverview Reusable Visual Broadcast Overlay Component for FanMessages (Mensajes a la afición).
 * Implements a high-performance Zero-Canvas architecture with GPU-accelerated CSS animations (translate3d, will-change),
 * state-driven flow transitions ('idle', 'entering', 'visible', 'fadingOut'), dynamic font auto-scaling,
 * QR code SVG generation, and support for all 11 visual banner formats and background modes.
 * 
 * @module components/Overlays/Overlays/FanMessages
 */

import React, { useState, useEffect, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Resolves a local or relative media URL into an absolute asset URL.
 * 
 * @function resolveMediaUrl
 * @param {string} mediaPath - Media path or URL.
 * @returns {string} Fully qualified URL.
 */
export const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const rawApiUrl = process.env.REACT_APP_API_URL;
    if (!rawApiUrl) {
        return mediaPath;
    }
    const apiBaseUrl = rawApiUrl.replace(/\/api\/?$/, '/');
    const cleanBase = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`;
    const cleanPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
    return `${cleanBase}${cleanPath}`;
};

/**
 * Returns a complete font-family fallback stack for the specified font name.
 * 
 * @function getFontFamilyStack
 * @param {string} fontName - Requested font family.
 * @returns {string} CSS font-family string.
 */
export const getFontFamilyStack = (fontName) => {
    switch (fontName) {
        case 'Bangers':
            return '"Bangers", cursive, "Montserrat", sans-serif';
        case 'Oswald':
            return '"Oswald", "Montserrat", sans-serif';
        case 'Arial Black':
        case 'Impact':
            return '"Arial Black", Impact, "Montserrat", sans-serif';
        case 'Montserrat':
        default:
            return '"Montserrat", -apple-system, BlinkMacSystemFont, sans-serif';
    }
};

/**
 * Extracts and normalizes the message entity from various WebSocket/REST payload structures.
 * 
 * @function extractMessageData
 * @param {Object} rawPayload - Raw incoming payload.
 * @returns {Object|null} Normalized message object.
 */
const extractMessageData = (rawPayload) => {
    if (!rawPayload) return null;
    if (rawPayload.message && typeof rawPayload.message === 'object') {
        return rawPayload.message;
    }
    if (rawPayload.data && typeof rawPayload.data === 'object') {
        if (rawPayload.data.message && typeof rawPayload.data.message === 'object') {
            return rawPayload.data.message;
        }
        return rawPayload.data;
    }
    return rawPayload;
};

/**
 * FanMessages Broadcast Overlay Component.
 * 
 * @function FanMessages
 * @param {Object} props - Component properties.
 * @param {Object} [props.data] - Real-time message payload or WebSocket packet.
 * @param {Object} [props.config] - Global overlay configuration (offsetY, scale, etc.).
 * @returns {React.ReactElement|null} Zero-Canvas overlay element or null if idle.
 */
export const FanMessages = ({ data, config }) => {
    const [verticalOffset, setVerticalOffset] = useState(0);
    const [flowState, setFlowState] = useState('idle'); // 'idle' | 'entering' | 'visible' | 'fadingOut'
    const [messageData, setMessageData] = useState(null);
    const [pendingData, setPendingData] = useState(null);

    const flowStateRef = React.useRef(flowState);
    useEffect(() => {
        flowStateRef.current = flowState;
    }, [flowState]);

    // 1. Sync vertical offset from config prop whenever it updates
    useEffect(() => {
        if (config) {
            if (config.offsetY !== undefined) {
                setVerticalOffset(Number(config.offsetY) || 0);
            } else if (config.verticalOffset !== undefined) {
                setVerticalOffset(Number(config.verticalOffset) || 0);
            }
        }
    }, [config]);

    // 2. Process incoming data & state transitions
    useEffect(() => {
        if (!data) return;

        // A. Direct config packet
        if (data.type === 'config' || data.action === 'config') {
            const newOffset = data.offsetY !== undefined
                ? data.offsetY
                : (data.globalOffsetY !== undefined ? data.globalOffsetY : data.data?.globalOffsetY);

            if (newOffset !== undefined) {
                setVerticalOffset(Number(newOffset) || 0);
            }

            // If config carries a message, update it seamlessly
            const nestedMsg = extractMessageData(data);
            if (nestedMsg && (nestedMsg.mainText || nestedMsg.title)) {
                setMessageData(nestedMsg);
            }
            return;
        }

        // B. Hide action or empty message command
        const isHideCommand = data.action === 'hide' || data.data?.action === 'hide' || data.isVisible === false || data.data?.isVisible === false;
        const payloadMsg = extractMessageData(data);
        const hasText = payloadMsg && (payloadMsg.mainText || payloadMsg.text || payloadMsg.title);

        if (isHideCommand || !hasText) {
            setPendingData(null);
            if (flowStateRef.current !== 'idle' && flowStateRef.current !== 'fadingOut') {
                setFlowState('fadingOut');
            }
            return;
        }

        // C. Update vertical offset from message payload if provided
        const potentialOffsetY = data.globalOffsetY !== undefined
            ? data.globalOffsetY
            : (data.offsetY !== undefined ? data.offsetY : payloadMsg.offsetY);

        if (potentialOffsetY !== undefined && !isNaN(Number(potentialOffsetY))) {
            setVerticalOffset(Number(potentialOffsetY));
        }

        // D. Show / Enqueue message
        if (flowStateRef.current === 'idle') {
            setMessageData(payloadMsg);
            setFlowState('entering');
        } else {
            setPendingData(payloadMsg);
            if (flowStateRef.current !== 'fadingOut') {
                setFlowState('fadingOut');
            }
        }
    }, [data]);

    // 3. Animation state machine timing (500ms entering / fadingOut)
    useEffect(() => {
        let timeoutId;
        if (flowState === 'entering') {
            timeoutId = setTimeout(() => {
                setFlowState('visible');
            }, 500);
        } else if (flowState === 'fadingOut') {
            timeoutId = setTimeout(() => {
                if (pendingData) {
                    setMessageData(pendingData);
                    setPendingData(null);
                    setFlowState('entering');
                } else {
                    setMessageData(null);
                    setFlowState('idle');
                }
            }, 500);
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [flowState, pendingData]);

    // Zero-Canvas: Return null immediately when idle to avoid DOM clutter and maximize rendering speed
    if (flowState === 'idle' || !messageData) {
        return null;
    }

    // Extract attributes with fallback defaults
    const {
        formatId = 1,
        mainText = '',
        subtitleText = '',
        backgroundType = 'transparent',
        backgroundUrl = '',
        backgroundColor = 'rgba(15, 23, 42, 0.95)',
        leftImageUrl = '',
        leftQrUrl = '',
        rightImageUrl = '',
        rightQrUrl = '',
        textColor = '#FFFFFF',
        subtitleColor = '#FFFFFF',
        fontFamily = 'Montserrat',
        fontSizePx = 44
    } = messageData;

    const currentFormatId = Number(formatId) >= 1 && Number(formatId) <= 11 ? Number(formatId) : 1;

    // Responsive typographic scaling based on text length
    const mainTextLength = (mainText || '').length;
    let baseFontSize = Number(fontSizePx) > 0 ? Number(fontSizePx) : 44;

    if (mainTextLength > 120) {
        baseFontSize = Math.max(18, Math.round(baseFontSize * 0.58));
    } else if (mainTextLength > 90) {
        baseFontSize = Math.max(20, Math.round(baseFontSize * 0.72));
    } else if (mainTextLength > 70) {
        baseFontSize = Math.max(24, Math.round(baseFontSize * 0.85));
    }

    const calculatedSubtitleSize = Math.max(16, Math.round(baseFontSize * 0.52));

    // Background style resolution
    const backgroundStyle = {};
    if (backgroundType === 'color' && backgroundColor) {
        backgroundStyle.backgroundColor = backgroundColor;
    } else if (backgroundType === 'image' && backgroundUrl) {
        // Handled via inner bg-image div for smooth rendering
    } else if (backgroundType === 'transparent') {
        backgroundStyle.backgroundImage = 'linear-gradient(90deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)';
        backgroundStyle.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.5)';
        backgroundStyle.borderTop = '2px solid rgba(255, 255, 255, 0.15)';
        backgroundStyle.borderBottom = '2px solid rgba(255, 255, 255, 0.15)';
    }

    // Render Left Side Element according to 11 formats
    const renderLeftElement = () => {
        // Formats with QR on left (7, 8)
        if (currentFormatId === 7 || currentFormatId === 8) {
            if (leftQrUrl) {
                return (
                    <div className="fan-messages-overlay__left">
                        <div className="fan-messages-overlay__qr-box">
                            <QRCodeSVG value={leftQrUrl} size={110} level="M" />
                        </div>
                    </div>
                );
            }
            if (leftImageUrl) {
                return (
                    <div className="fan-messages-overlay__left">
                        <img
                            src={resolveMediaUrl(leftImageUrl)}
                            alt="Logo Izquierdo"
                            className="fan-messages-overlay__side-image"
                        />
                    </div>
                );
            }
            return <div className="fan-messages-overlay__left" />;
        }

        // Formats with Image on left (1, 3, 4, 10)
        if ([1, 3, 4, 10].includes(currentFormatId)) {
            if (leftImageUrl) {
                return (
                    <div className="fan-messages-overlay__left">
                        <img
                            src={resolveMediaUrl(leftImageUrl)}
                            alt="Logo Izquierdo"
                            className="fan-messages-overlay__side-image"
                        />
                    </div>
                );
            }
            if (leftQrUrl) {
                return (
                    <div className="fan-messages-overlay__left">
                        <div className="fan-messages-overlay__qr-box">
                            <QRCodeSVG value={leftQrUrl} size={110} level="M" />
                        </div>
                    </div>
                );
            }
            return <div className="fan-messages-overlay__left" />;
        }

        // Formats with NO left element (2, 5, 6, 9, 11)
        return null;
    };

    // Render Right Side Element according to 11 formats
    const renderRightElement = () => {
        // Formats with QR on right (7, 9)
        if (currentFormatId === 7 || currentFormatId === 9) {
            if (rightQrUrl) {
                return (
                    <div className="fan-messages-overlay__right">
                        <div className="fan-messages-overlay__qr-box">
                            <QRCodeSVG value={rightQrUrl} size={110} level="M" />
                        </div>
                    </div>
                );
            }
            if (rightImageUrl) {
                return (
                    <div className="fan-messages-overlay__right">
                        <img
                            src={resolveMediaUrl(rightImageUrl)}
                            alt="Logo Derecho"
                            className="fan-messages-overlay__side-image"
                        />
                    </div>
                );
            }
            return <div className="fan-messages-overlay__right" />;
        }

        // Formats with Image on right (1, 3, 4, 11)
        if ([1, 3, 4, 11].includes(currentFormatId)) {
            if (rightImageUrl) {
                return (
                    <div className="fan-messages-overlay__right">
                        <img
                            src={resolveMediaUrl(rightImageUrl)}
                            alt="Logo Derecho"
                            className="fan-messages-overlay__side-image"
                        />
                    </div>
                );
            }
            if (rightQrUrl) {
                return (
                    <div className="fan-messages-overlay__right">
                        <div className="fan-messages-overlay__qr-box">
                            <QRCodeSVG value={rightQrUrl} size={110} level="M" />
                        </div>
                    </div>
                );
            }
            return <div className="fan-messages-overlay__right" />;
        }

        // Formats with NO right element (2, 5, 6, 8, 10)
        return null;
    };

    // Render Center Texts based on layout format
    const renderCenterContent = () => {
        const textStyle = {
            color: textColor || '#FFFFFF',
            fontFamily: getFontFamilyStack(fontFamily),
            fontSize: `${baseFontSize}px`,
            letterSpacing: fontFamily === 'Bangers' ? '1.5px' : '0.5px'
        };

        const subStyle = {
            color: subtitleColor || '#FFFFFF',
            fontFamily: getFontFamilyStack(fontFamily),
            fontSize: `${calculatedSubtitleSize}px`
        };

        // Formats with NO subtitle (1, 2)
        if (currentFormatId === 1 || currentFormatId === 2) {
            return (
                <div className="fan-messages-overlay__center">
                    <p className="fan-messages-overlay__main-text" style={textStyle}>
                        {mainText}
                    </p>
                </div>
            );
        }

        // Formats with SUBTITLE ON TOP (3, 5, 7, 8, 9, 10, 11)
        if ([3, 5, 7, 8, 9, 10, 11].includes(currentFormatId)) {
            return (
                <div className="fan-messages-overlay__center">
                    {subtitleText && (
                        <p className="fan-messages-overlay__subtitle-text" style={subStyle}>
                            {subtitleText}
                        </p>
                    )}
                    <p className="fan-messages-overlay__main-text" style={textStyle}>
                        {mainText}
                    </p>
                </div>
            );
        }

        // Formats with SUBTITLE ON BOTTOM (4, 6)
        if (currentFormatId === 4 || currentFormatId === 6) {
            return (
                <div className="fan-messages-overlay__center">
                    <p className="fan-messages-overlay__main-text" style={textStyle}>
                        {mainText}
                    </p>
                    {subtitleText && (
                        <p className="fan-messages-overlay__subtitle-text" style={subStyle}>
                            {subtitleText}
                        </p>
                    )}
                </div>
            );
        }

        return (
            <div className="fan-messages-overlay__center">
                <p className="fan-messages-overlay__main-text" style={textStyle}>
                    {mainText}
                </p>
            </div>
        );
    };

    return (
        <div
            className="fan-messages-overlay"
            style={{ bottom: `${verticalOffset}px` }}
        >
            <div
                className={`fan-messages-overlay__banner fan-messages-overlay__banner--${flowState}`}
                style={backgroundStyle}
            >
                {/* Background Video Layer */}
                {backgroundType === 'video' && backgroundUrl && (
                    <video
                        src={resolveMediaUrl(backgroundUrl)}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="fan-messages-overlay__bg-video"
                    />
                )}

                {/* Background Image Layer */}
                {backgroundType === 'image' && backgroundUrl && (
                    <div
                        className="fan-messages-overlay__bg-image"
                        style={{ backgroundImage: `url(${resolveMediaUrl(backgroundUrl)})` }}
                    />
                )}

                {/* Content Layer */}
                <div className="fan-messages-overlay__content">
                    {renderLeftElement()}
                    {renderCenterContent()}
                    {renderRightElement()}
                </div>
            </div>
        </div>
    );
};

export default FanMessages;
